import React, { useState } from 'react';

import { Badge } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBinoculars,
  faCalendar,
  faEdit,
  faEnvelope,
  faPhone,
  faStickyNote,
  type IconDefinition
} from '@fortawesome/pro-regular-svg-icons';

import confirm from 'components/confirm';
import Dropdown from 'components/dropdown';
import { activityHeaderText } from 'bundles/apex/activities/activity/helpers';
import { formatDate } from 'helpers/formatters/date_time_formats';
import Avatars from 'bundles/connect/components/profile/avatars';
import ActivityParent from './parent';
import ActivityEmailThread from 'bundles/apex/activities/activity/email_thread';
import ActivityBody from 'bundles/apex/activities/activity/body';
import ActivityModal from 'bundles/apex/activities/modal';
import ReparentingMenuItems from 'bundles/crm/parents/reparenting_menu_items';
import { deleteActivity } from 'bundles/apex/activities/form/helpers';

import type { Activity } from 'bundles/apex/activities/types';
import type { MenuOptions } from 'bundles/apex/activities/types';
import type { Parent, ParentOption } from 'bundles/crm/types';

import './styles.scss';

type ActivityItemProps = {
  activity: Activity;
  showParents?: boolean | 'path';
  showActions?: boolean;
  currentParent?: Parent | Parent[];
  options?: {
    parents?: ParentOption[];
  };
  menuOptions?: MenuOptions;
  hasFilters?: boolean;
  hideAfterReparenting?: boolean;
  forUserId?: number;
  isBackofficeAdmin?: boolean;
  tagText?: string;
  onDelete?: (activity: Activity) => void;
  onSave?: (activity: Activity) => void;
}

export default function ActivityItem({
  activity,
  currentParent,
  options,
  menuOptions,
  hasFilters,
  hideAfterReparenting,
  showParents,
  showActions = true,
  forUserId,
  isBackofficeAdmin,
  tagText,
  onDelete,
  onSave
}: ActivityItemProps) {
  if (!activity) return null;

  const [showEditModal, setShowEditModal] = useState(false);
  const header = activityHeaderText(activity);
  let icon: IconDefinition | null = null;
  let canEdit = !activity.hasCrmEmail;
  let canDelete = true;

  let isCreator = false;
  if (forUserId != null) {
    isCreator = forUserId === activity.creator.id;
    canEdit = isCreator && !activity.hasCrmEmail;
    canDelete = isCreator || Boolean(isBackofficeAdmin);
  }

  const handleDelete = (event) => {
    event.stopPropagation();

    let title, message;
    if (activity.isEmailThread) {
      title = 'Delete Thread?';
      message = 'Are you sure you want to delete this thread and all of its messages?';
    } else {
      title = 'Delete Activity?';
      message = 'Are you sure you want to delete this activity?';
    }

    confirm(message, {
      buttonLabel: 'Delete',
      buttonStyle: 'danger',
      title: title
    }).then(async () => {
      const deletedActivity = await deleteActivity(activity);
      if (onDelete) {
        onDelete(deletedActivity);
      }
    }).catch(() => { });
  };

  switch (activity.activityType) {
  case 'call':
    icon = faPhone;
    break;
  case 'message':
    icon = faEnvelope;
    break;
  case 'meeting':
    icon = faCalendar;
    break;
  case 'showing':
    icon = faBinoculars;
    break;
  case 'note':
    icon = faStickyNote;
    break;
  default:
    icon = faEnvelope;
  }

  return (
    <div className="deal-activity gap-3 d-flex align-self-stretch">
      <div className="timeline">
        <Badge bg="light" className="rounded-circle border p-2 position-relative" style={{ zIndex: 1 }}>
          <FontAwesomeIcon color="black" icon={icon} />
        </Badge>
      </div>
      <div className="d-flex flex-column gap-2 flex-grow-1 py-2">
        <div className="d-flex flex-row gap-2 align-items-center justify-content-between">
          <span className="fs-large fw-semibold text-truncate">{header}</span>
          <div className="d-flex flex-row gap-2 align-items-center">
            <span className="text-muted">{formatDate(activity.date)}</span>
            <Avatars avatars={[]} />
          </div>
        </div>
        {showParents && (
          <ActivityParent
            activity={activity}
            asLink={showParents === 'path'}
            currentParent={currentParent}
            options={options}
          />
        )}
        {activity.isEmailThread
          ? <ActivityEmailThread activity={activity} autoExpand={hasFilters} />
          : <ActivityBody activity={activity} />
        }
      </div>
      {showActions && (
        <Dropdown className="pt-2" size="sm">
          <Dropdown.Item disabled={!canEdit} onClick={() => setShowEditModal(true)}>
            <FontAwesomeIcon icon={faEdit} />
            Edit Activity
          </Dropdown.Item>
          {isCreator &&
            <ReparentingMenuItems
              parentOptions={menuOptions?.parents}
              record={activity}
              onReparent={activity => hideAfterReparenting ? onDelete?.(activity) : onSave?.(activity)}
            />
          }
          <Dropdown.Item disabled={!canDelete} isDelete onClick={handleDelete} />
        </Dropdown>
      )}

      <ActivityModal
        activity={activity}
        header="Edit activity"
        show={showEditModal}
        tagText={tagText}
        onHide={() => setShowEditModal(false)}
        onSave={onSave}
      />
    </div>
  );

}
